/**
 * GroupedSidebar — narrow-default + hover-expand + pinnable navigation.
 *
 * Authority: docs/superpowers/plans/2026-04-21-inbox-cockpit-and-sidebar-hover.md §4.2
 *            docs/skills/DESIGN_LANGUAGE_SKILL.md
 *
 * Behaviour:
 *   - Collapsed default (64px). Icons only, tooltip on hover per item.
 *   - Hover-expand to 232px after 120ms enter-intent delay. Snap back to
 *     64px 200ms after mouse-leave. Width transition 180ms ease-out.
 *   - Pinned: isPinned flag (owned by AppLayout, persisted via useSidebarPinned
 *     hook) locks the rail open. Pin affordance lives in the rail header.
 *   - Section headings are static when expanded. No chevron collapse —
 *     previous V5 Collapsible sections were removed per plan §4.2.
 *
 * Tokens: DS_SPACING.sidebarWidth{Collapsed,Expanded}, DS_MOTION.sidebar,
 *         DS_COLORS, DS_TYPOGRAPHY.
 *
 * Layout: Position fixed so the hover-expanded rail overlays main content
 *         without shifting it. AppLayout pads main content by collapsed width
 *         when unpinned, expanded width when pinned (push, not overlay).
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useLocation } from 'react-router-dom';
import {
  Tray, Lightbulb, MagnifyingGlass, EnvelopeSimple, ChatCircle, ChartLineUp,
  Books, UsersThree, Rocket, Handshake, Target, Briefcase, Sparkle, Tag, Gear,
  LinkedinLogo, FunnelSimple, Graph, PushPin, PushPinSlash,
} from '@phosphor-icons/react';
import { Separator } from '@/components/ui/separator';
import { DS_COLORS, DS_TYPOGRAPHY, DS_SPACING, DS_MOTION } from '@/config/ui-invariants';
import { ROUTES } from '@/config/product-contract';
import { SHARED_UI_LABELS } from '@/config/task-display-config';

const SIDEBAR_ICON_MAP = {
  Tray, Lightbulb, MagnifyingGlass, EnvelopeSimple, ChatCircle, ChartLineUp,
  Books, UsersThree, Rocket, Handshake, Target, Briefcase, Sparkle, Tag, Gear,
  LinkedinLogo, FunnelSimple, Graph,
};

// ============================================================
// NAV ITEM — single sidebar link. Active: DS_COLORS.surface.warm bg +
// 3px DS_COLORS.border.brand left accent. Labels fade in after the
// rail width transition so text doesn't horizontally slide.
// ============================================================
function NavItem({ item, isExpanded, badgeCount = 0 }) {
  const location = useLocation();
  const isActive = location.pathname === item.path ||
    (item.path !== ROUTES.ROOT && location.pathname.startsWith(item.path));

  const IconComponent = SIDEBAR_ICON_MAP[item.icon];

  return (
    <Link
      to={item.path}
      aria-current={isActive ? 'page' : undefined}
      aria-label={item.name}
      title={!isExpanded ? item.name : undefined}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: `${DS_SPACING.buttonGap}px`,
        minHeight: `${DS_SPACING.minTouchTarget}px`,
        paddingLeft: isExpanded ? `${DS_SPACING.labelToContent}px` : '0',
        paddingRight: `${DS_SPACING.buttonGap}px`,
        paddingTop: '8px',
        paddingBottom: '8px',
        borderRadius: '6px',
        textDecoration: 'none',
        transition: `background ${DS_MOTION.hover.rowDuration}ms ${DS_MOTION.hover.rowEase}`,
        position: 'relative',
        background: isActive ? DS_COLORS.surface.warm : 'transparent',
        borderLeft: isActive
          ? `3px solid ${DS_COLORS.border.brand}`
          : '3px solid transparent',
        justifyContent: isExpanded ? 'flex-start' : 'center',
      }}
      onMouseEnter={(e) => {
        if (!isActive) e.currentTarget.style.background = DS_COLORS.surface.warm;
      }}
      onMouseLeave={(e) => {
        if (!isActive) e.currentTarget.style.background = 'transparent';
      }}
    >
      <span
        style={{
          width: '20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          color: isActive ? DS_COLORS.text.brand : DS_COLORS.text.secondary,
        }}
      >
        {IconComponent
          ? <IconComponent weight={isActive ? 'bold' : 'regular'} size={18} />
          : null}
      </span>

      {isExpanded && (
        <span
          style={{
            ...DS_TYPOGRAPHY.actionBtn,
            color: isActive ? DS_COLORS.text.brand : DS_COLORS.text.secondary,
            flex: 1,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            opacity: 0,
            animation: `accrezeoSidebarLabelFade ${DS_MOTION.sidebar.labelFadeDurationMs}ms ease-out ${DS_MOTION.sidebar.labelFadeDelayMs}ms forwards`,
          }}
        >
          {item.name}
        </span>
      )}

      {badgeCount > 0 && isExpanded && (
        <span
          aria-label={`${badgeCount} pending`}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '999px',
            background: DS_COLORS.text.brand,
            color: DS_COLORS.surface.white,
            fontSize: '10px',
            fontWeight: 700,
            minWidth: '18px',
            height: '18px',
            padding: '0 4px',
            flexShrink: 0,
          }}
        >
          {badgeCount > 9 ? '9+' : badgeCount}
        </span>
      )}
      {badgeCount > 0 && !isExpanded && (
        <span
          aria-label={`${badgeCount} pending`}
          style={{
            position: 'absolute',
            top: '6px',
            right: '6px',
            width: '8px',
            height: '8px',
            borderRadius: '999px',
            background: DS_COLORS.text.brand,
          }}
        />
      )}
    </Link>
  );
}

NavItem.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    path: PropTypes.string.isRequired,
    icon: PropTypes.string,
  }).isRequired,
  isExpanded: PropTypes.bool.isRequired,
  badgeCount: PropTypes.number,
};

// ============================================================
// SIDEBAR SECTION — static heading + nav items. No chevron.
// Heading hidden when collapsed (icon-only rail).
// ============================================================
function SidebarSection({ section, isExpanded, badgeCounts = {} }) {
  return (
    <div style={{ marginTop: '8px' }}>
      {isExpanded && (
        <div
          style={{
            padding: `${DS_SPACING.labelToContent}px ${DS_SPACING.labelToContent}px 4px`,
            opacity: 0,
            animation: `accrezeoSidebarLabelFade ${DS_MOTION.sidebar.labelFadeDurationMs}ms ease-out ${DS_MOTION.sidebar.labelFadeDelayMs}ms forwards`,
          }}
        >
          <span
            style={{
              ...DS_TYPOGRAPHY.microLabel,
              color: DS_COLORS.text.muted,
            }}
          >
            {section.label}
          </span>
        </div>
      )}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '2px',
          padding: isExpanded ? '0 8px' : '0 4px',
        }}
      >
        {(section.items || []).map((item) => (
          <NavItem
            key={item.id}
            item={item}
            isExpanded={isExpanded}
            badgeCount={badgeCounts[item.id] || 0}
          />
        ))}
      </div>
    </div>
  );
}

SidebarSection.propTypes = {
  section: PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    items: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  isExpanded: PropTypes.bool.isRequired,
  badgeCounts: PropTypes.object,
};

// ============================================================
// GROUPED SIDEBAR — narrow-default rail with hover-expand + pin.
// Owns transient isHovering; receives persisted isPinned from AppLayout.
// isExpanded = isPinned || isHovering. Position fixed so hover-expand
// overlays main content without layout shift.
// ============================================================
export function GroupedSidebar({
  sections,
  settingsItem,
  badgeCounts = {},
  isPinned = false,
  onTogglePin,
}) {
  const [isHovering, setIsHovering] = useState(false);
  const enterTimerRef = useRef(null);
  const leaveTimerRef = useRef(null);

  const clearTimers = useCallback(() => {
    if (enterTimerRef.current) {
      clearTimeout(enterTimerRef.current);
      enterTimerRef.current = null;
    }
    if (leaveTimerRef.current) {
      clearTimeout(leaveTimerRef.current);
      leaveTimerRef.current = null;
    }
  }, []);

  useEffect(() => clearTimers, [clearTimers]);

  const handleMouseEnter = useCallback(() => {
    if (isPinned) return;
    if (leaveTimerRef.current) {
      clearTimeout(leaveTimerRef.current);
      leaveTimerRef.current = null;
    }
    if (enterTimerRef.current || isHovering) return;
    enterTimerRef.current = setTimeout(() => {
      setIsHovering(true);
      enterTimerRef.current = null;
    }, DS_MOTION.sidebar.hoverEnterDelayMs);
  }, [isPinned, isHovering]);

  const handleMouseLeave = useCallback(() => {
    if (isPinned) return;
    if (enterTimerRef.current) {
      clearTimeout(enterTimerRef.current);
      enterTimerRef.current = null;
    }
    if (leaveTimerRef.current) return;
    leaveTimerRef.current = setTimeout(() => {
      setIsHovering(false);
      leaveTimerRef.current = null;
    }, DS_MOTION.sidebar.hoverLeaveDelayMs);
  }, [isPinned]);

  const handleFocusCapture = useCallback(() => {
    // Keyboard focus inside the rail should expand, mirroring hover behaviour.
    if (isPinned || isHovering) return;
    clearTimers();
    setIsHovering(true);
  }, [isPinned, isHovering, clearTimers]);

  const handleBlurCapture = useCallback((e) => {
    if (isPinned) return;
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setIsHovering(false);
  }, [isPinned]);

  const isExpanded = isPinned || isHovering;
  const widthPx = isExpanded
    ? DS_SPACING.sidebarWidthExpanded
    : DS_SPACING.sidebarWidthCollapsed;

  return (
    <>
      {/* Keyframes for label fade-in. Injected once per render tree; small enough to inline. */}
      <style>{`
        @keyframes accrezeoSidebarLabelFade {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
      <aside
        data-testid="accrezeo-sidebar"
        data-expanded={isExpanded ? 'true' : 'false'}
        data-pinned={isPinned ? 'true' : 'false'}
        aria-label={SHARED_UI_LABELS.mainNavigation}
        aria-expanded={isExpanded}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onFocusCapture={handleFocusCapture}
        onBlurCapture={handleBlurCapture}
        style={{
          width: `${widthPx}px`,
          flexShrink: 0,
          background: DS_COLORS.surface.white,
          borderRight: `1px solid ${DS_COLORS.border.default}`,
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          position: 'fixed',
          top: 0,
          left: 0,
          zIndex: 50,
          transition: `width ${DS_MOTION.sidebar.widthDurationMs}ms ${DS_MOTION.sidebar.widthEase}`,
          overflow: 'hidden',
        }}
      >
        {/* Header: logo + pin toggle */}
        <div
          style={{
            height: '64px',
            display: 'flex',
            alignItems: 'center',
            padding: isExpanded ? `0 ${DS_SPACING.labelToContent}px` : '0',
            borderBottom: `1px solid ${DS_COLORS.border.default}`,
            justifyContent: isExpanded ? 'space-between' : 'center',
            flexShrink: 0,
            gap: '8px',
          }}
        >
          <Link
            to={ROUTES.ROOT}
            aria-label={SHARED_UI_LABELS.accrezeoHome}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              textDecoration: 'none',
              minWidth: 0,
            }}
          >
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden="true">
              <rect width="28" height="28" rx="6" fill={DS_COLORS.text.brand} />
              <path d="M7 20L14 8L21 20H7Z" fill="none" stroke="white" strokeWidth="1.8" strokeLinejoin="round" />
              <circle cx="14" cy="16" r="2" fill="white" />
            </svg>
            {isExpanded && (
              <span
                style={{
                  ...DS_TYPOGRAPHY.actionBtn,
                  fontSize: '17px',
                  color: DS_COLORS.text.brand,
                  fontWeight: 700,
                  opacity: 0,
                  animation: `accrezeoSidebarLabelFade ${DS_MOTION.sidebar.labelFadeDurationMs}ms ease-out ${DS_MOTION.sidebar.labelFadeDelayMs}ms forwards`,
                }}
              >
                Accrezeo
              </span>
            )}
          </Link>

          {isExpanded && onTogglePin && (
            <button
              type="button"
              onClick={onTogglePin}
              aria-label={isPinned ? 'Unpin sidebar' : 'Pin sidebar open'}
              aria-pressed={isPinned}
              title={isPinned ? 'Unpin sidebar' : 'Pin sidebar open'}
              style={{
                background: 'transparent',
                border: 'none',
                padding: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: isPinned ? DS_COLORS.text.brand : DS_COLORS.text.muted,
                borderRadius: '6px',
                minWidth: '32px',
                minHeight: '32px',
                opacity: 0,
                animation: `accrezeoSidebarLabelFade ${DS_MOTION.sidebar.labelFadeDurationMs}ms ease-out ${DS_MOTION.sidebar.labelFadeDelayMs}ms forwards`,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = DS_COLORS.surface.warm;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
              }}
            >
              {isPinned
                ? <PushPin size={16} weight="fill" />
                : <PushPinSlash size={16} weight="regular" />}
            </button>
          )}
        </div>

        {/* Navigation sections */}
        <nav
          aria-label={SHARED_UI_LABELS.primaryNavigation}
          style={{
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            paddingTop: '4px',
            paddingBottom: '4px',
          }}
        >
          {sections.map((section) => (
            <SidebarSection
              key={section.id}
              section={section}
              isExpanded={isExpanded}
              badgeCounts={badgeCounts}
            />
          ))}
        </nav>

        {/* Settings footer */}
        <div style={{ flexShrink: 0, padding: isExpanded ? '0 8px 12px' : '0 4px 12px' }}>
          <Separator style={{ marginBottom: '8px' }} />
          {settingsItem && (
            <NavItem item={settingsItem} isExpanded={isExpanded} />
          )}
        </div>
      </aside>
    </>
  );
}

GroupedSidebar.propTypes = {
  sections: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    items: PropTypes.array,
  })).isRequired,
  settingsItem: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    path: PropTypes.string.isRequired,
    icon: PropTypes.string,
  }),
  badgeCounts: PropTypes.object,
  isPinned: PropTypes.bool,
  onTogglePin: PropTypes.func,
};

export default GroupedSidebar;
